import { useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import interviewsServices, {
  AddInterviewQuestion,
  DeleteInterviewQuesiton,
  UpdateInterviewQuesiton,
} from '@/services/interviews';
import { InterviewType } from '@/types/api';
import InterviewQuestionsManagerComponent, {
  CustomQuestionType,
} from './InterviewQuestionsManager.component';

type InterviewQuestionsManagerProps = {
  interview: InterviewType;
  canManageQuestions?: boolean;
  onRefetchInterview: () => void;
};

export default function InterviewQuestionsManager({
  interview,
  canManageQuestions = true,
  onRefetchInterview,
}: InterviewQuestionsManagerProps): JSX.Element {
  const toast = useToastContext();
  const [idToDelete, setIdToDelete] = useState('');

  const addInterviewQuestionMutation = useApiSubmit<any, AddInterviewQuestion>(
    interviewsServices.addQuestionInterview
  );

  const deleteInterviewQuestionMutation = useApiSubmit<any, DeleteInterviewQuesiton>(
    interviewsServices.deleteQuestionInterview
  );

  const updateInterviewQuestionMutation = useApiSubmit<any, UpdateInterviewQuesiton>(
    interviewsServices.updateQuestionInterview
  );

  const [questions, setQuestions] = useState<CustomQuestionType[]>(
    interview.questions.map((question) => ({
      ...question,
      isEditing: false,
    }))
  );

  const onAddNewQuestions = (): void => {
    setQuestions((prev) =>
      prev.concat({
        questionText: '',
        isEditing: true,
        generateQuestionByAI: '',
      })
    );
  };

  const onEditQuestion = (index: number): void => {
    setQuestions((prev) =>
      prev.map((question, questionIndex) => {
        if (index !== questionIndex) {
          return question;
        }

        return {
          ...question,
          isEditing: true,
          draft: question.questionText,
        };
      })
    );
  };

  const onCancelEditingQuestion = (index: number): void => {
    setQuestions((prev) =>
      prev
        .map((question, questionIndex) => {
          if (index !== questionIndex) {
            return question;
          }

          return {
            ...question,
            isEditing: false,
            questionText: question.draft,
            draft: '',
          };
        })
        .filter((question, questionIndex) => {
          return index !== questionIndex || !!question.questionText;
        })
    );
  };

  const updateQuestionAfterSubmit = (index: number, id: string): void => {
    setQuestions((prev) =>
      prev.map((question, questionIndex) => {
        if (index !== questionIndex) {
          return question;
        }
        return {
          ...question,
          id,
          isEditing: false,
        };
      })
    );
  };

  const onAddNewInterviewQuestion = async (index: number): Promise<void> => {
    const { questionText, id, generateQuestionByAI } = questions[index];
    const actionsVerb = id ? 'updated' : 'added';
    try {
      const methodToCall = id ? updateInterviewQuestionMutation : addInterviewQuestionMutation;
      const newQuestion = await methodToCall.mutateAsync({
        questionText: questionText ?? '',
        interviewId: interview.id,
        interviewQuestionId: id,
        generateQuestionByAI: generateQuestionByAI?.toLowerCase().replace(/\s+/g, ''),
      });

      setQuestions((prev) =>
        prev.map((question, questionIndex) => {
          if (index !== questionIndex) {
            return question;
          }
          return {
            ...question,
            id: newQuestion.id,
            questionText: newQuestion.questionText || question.questionText,
            isEditing: false,
          };
        })
      );

      onRefetchInterview();
      toast.showSuccessFeedback(`The question has been ${actionsVerb} to the interview.`);
    } catch {
      toast.showErrorFeedback(
        `There was a problem to ${actionsVerb} the question to the interview.`
      );
    }
  };

  const onDeleteInterviewQuestion = async (): Promise<void> => {
    try {
      const id = idToDelete;
      await deleteInterviewQuestionMutation.mutateAsync({
        interviewId: interview.id,
        interviewQuestionId: id,
      });
      setIdToDelete('');

      setQuestions((prev) =>
        prev.filter((question) => {
          return id !== question.id;
        })
      );
      onRefetchInterview();
      toast.showSuccessFeedback('The question has been deleted of the interview.');
    } catch {
      toast.showErrorFeedback('There was a problem to delete the question of the interview.');
    }
  };

  const onConfirmQuestionDelete = (id: string): void => {
    setIdToDelete(id);
  };

  const onChangeQuestion = (event: any, index: number): void => {
    const {
      target: { value, generateQuestionByAI },
    } = event;

    setQuestions((prev) =>
      prev.map((question, questionIndex) => {
        if (index !== questionIndex) {
          return question;
        }
        return {
          ...question,
          questionText: value,
          generateQuestionByAI,
        };
      })
    );
  };

  return (
    <InterviewQuestionsManagerComponent
      canManageQuestions={canManageQuestions}
      questions={questions}
      onEditQuestion={onEditQuestion}
      onCancelEditingQuestion={onCancelEditingQuestion}
      onChangeQuestion={onChangeQuestion}
      onAddNewInterviewQuestion={onAddNewInterviewQuestion}
      onConfirmQuestionDelete={onConfirmQuestionDelete}
      onAddNewQuestions={onAddNewQuestions}
      idToDelete={idToDelete}
      setIdToDelete={setIdToDelete}
      onDeleteInterviewQuestion={onDeleteInterviewQuestion}
    />
  );
}
